import { Injectable } from '@angular/core';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { switchMap, tap } from 'rxjs/operators';
import { FirebaseAuthClient } from '@apps/clos-sessions/services/firebase-auth-client';
import { AuthApi } from './auth.api';
import { PatchUser, RefreshToken, SignIn, SignOut } from './actions';
import { ClosUser, ProviderProfile } from './types';

const AUTH_STATE_NAME = 'auth';

export class AuthStateModel {
  activeUsers: ClosUser[];

  tokens: string[];

  authProvidersAdditionalData: Partial<ProviderProfile>[];
}

@State<AuthStateModel>({
  name: AUTH_STATE_NAME,
  defaults: {
    activeUsers: [],
    tokens: [],
    authProvidersAdditionalData: [],
  },
})
@Injectable()
export class AuthState {
  static readonly stateName = AUTH_STATE_NAME;

  static token = createSelector([AuthState], ({ tokens: [token] }: AuthStateModel) => token);

  static user = createSelector([AuthState], ({ activeUsers: [user] }: AuthStateModel) => user);

  static userProfile = createSelector(
    [AuthState],
    ({ authProvidersAdditionalData }: AuthStateModel) =>
      authProvidersAdditionalData.reduce((acc, cur) => ({ ...acc, ...cur }), {}),
  );

  static isSignedIn = createSelector(
    [AuthState.user, AuthState.token],
    (user: ClosUser, token: string) => !!(token && user),
  );

  constructor(
    protected firebaseAuthClient: FirebaseAuthClient,
    protected authApi: AuthApi,
  ) {}

  @Action(PatchUser)
  patchUser({ setState }: StateContext<AuthStateModel>, { payload }: PatchUser): void {
    setState(
      patch({
        activeUsers: updateItem<ClosUser>((user) => user.id === payload.id, payload),
      }),
    );
  }

  @Action(SignIn)
  protected onSignIn({ patchState }: StateContext<AuthStateModel>, { payload }: SignIn) {
    return this.firebaseAuthClient.signIn({ providerType: payload.provider }).pipe(
      tap(({ token, providerProfiles }) => {
        patchState({ tokens: [token], authProvidersAdditionalData: providerProfiles });
      }),
      switchMap(({ token }) => this.authApi.login(token)),
      tap((user) => patchState({ activeUsers: [user] })),
    );
  }

  @Action(SignOut)
  protected onSignOut({ setState }: StateContext<AuthStateModel>) {
    return this.firebaseAuthClient.signOut().pipe(
      tap(() =>
        setState({
          authProvidersAdditionalData: [],
          tokens: [],
          activeUsers: [],
        }),
      ),
    );
  }

  @Action(RefreshToken)
  protected onRefreshToken({ patchState }: StateContext<AuthStateModel>) {
    return this.firebaseAuthClient.refreshToken().pipe(
      tap(({ token }) =>
        patchState({
          tokens: [token],
        }),
      ),
    );
  }
}
